import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ForYouSection, RecentlyViewedSection } from './RecommendationSections';
import { Scan, Palette, ShieldCheck, Users, Sparkles, CheckCircle, Star, Package, Menu, X, ShoppingBag, User, Scissors, HeadphonesIcon, ChevronRight, LogOut, LogIn, Shield, Heart, Gift } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const trustBadges = [
    { icon: Sparkles, label: 'AI Sizing', value: '95% accuracy in 30 sec' },
    { icon: Users, label: 'Master Tailors', value: '500+ verified experts' },
    { icon: CheckCircle, label: 'Free Alterations', value: 'Within 7 days' },
    { icon: ShieldCheck, label: 'Money-Back', value: 'If not satisfied' },
  ];

  const socialProof = [
    { icon: Star, value: '25,000+', label: 'Happy Customers' },
    { icon: Users, value: '500+', label: 'Expert Tailors' },
    { icon: Package, value: '50,000+', label: 'Orders Delivered' },
  ];

  const isPrivileged = user && (user.role === 'admin' || user.role === 'founder');
  const menuItems = [
    { icon: Heart, label: 'Wishlist', path: '/wishlist', testId: 'menu-wishlist' },
    { icon: ShoppingBag, label: 'My Orders', path: '/my-orders', testId: 'menu-my-orders' },
    { icon: Gift, label: 'Refer & Earn', path: '/referrals', testId: 'menu-referrals' },
    { icon: User, label: user ? user.name || 'Account' : 'Sign In', path: user ? null : '/auth', testId: 'menu-account' },
    { icon: Scissors, label: 'Tailor Login', path: '/tailor-login', testId: 'menu-tailor-login' },
    ...(isPrivileged ? [{ icon: Shield, label: 'Admin', path: '/admin', testId: 'menu-admin' }] : []),
    { icon: HeadphonesIcon, label: 'Help Center', path: '/info/help', testId: 'menu-support' },
  ];

  return (
    <div 
      data-testid="landing-page"
      className="min-h-screen"
      style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}
    >
      {/* Header with Menu */}
      <div 
        className="sticky top-0 z-50 flex items-center justify-between px-6 py-4"
        style={{ backgroundColor: 'rgba(250, 249, 246, 0.95)', backdropFilter: 'blur(12px)' }}
      >
        <img
          data-testid="landing-header-logo"
          src="/lunora_logo.png"
          alt="Lunora's"
          className="h-9 w-auto"
          style={{ display: 'block' }}
        />
        <button
          data-testid="header-menu-btn"
          onClick={() => setMenuOpen(true)}
          className="p-2 -mr-2 transition-colors"
          style={{ color: '#0F0F0F' }}
        >
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>
      </div>

      {/* Slide-in Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-[100]" data-testid="menu-overlay">
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => setMenuOpen(false)}
            style={{ backdropFilter: 'blur(4px)' }}
          />
          <div 
            className="absolute right-0 top-0 h-full w-[280px] sm:w-[320px] flex flex-col shadow-2xl"
            style={{ 
              backgroundColor: '#FAF9F6',
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            {/* Menu Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: '#E5E5E5' }}>
              <span 
                className="text-lg"
                style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}
              >
                Menu
              </span>
              <button
                data-testid="menu-close-btn"
                onClick={() => setMenuOpen(false)}
                className="p-1"
              >
                <X className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 py-4">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  data-testid={item.testId}
                  onClick={() => {
                    setMenuOpen(false);
                    if (item.path) navigate(item.path);
                  }}
                  className="w-full flex items-center gap-4 px-6 py-4 transition-colors hover:bg-black/[0.03]"
                >
                  <item.icon className="w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />
                  <span className="text-sm flex-1 text-left" style={{ color: '#0F0F0F', letterSpacing: '0.02em' }}>
                    {item.label}
                  </span>
                  <ChevronRight className="w-4 h-4" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
                </button>
              ))}
            </div>

            {/* Menu Footer */}
            <div className="px-6 py-5 border-t space-y-2" style={{ borderColor: '#E5E5E5' }}>
              {user && (
                <button
                  data-testid="menu-logout-btn"
                  onClick={async () => { setMenuOpen(false); await logout(); }}
                  className="w-full py-3 text-xs tracking-[0.1em] uppercase text-center border"
                  style={{ borderColor: '#E5E5E5', color: '#991B1B' }}
                >
                  Sign Out
                </button>
              )}
              {!user && (
                <button
                  data-testid="menu-signin-btn"
                  onClick={() => { setMenuOpen(false); navigate('/auth'); }}
                  className="w-full py-3 text-xs tracking-[0.1em] uppercase text-center text-white"
                  style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}
                >
                  Sign In / Create Account
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="px-6 pt-8 pb-10">
        {/* Urgency Banner */}
        <div className="flex items-center justify-center gap-2 mb-6 py-2 px-4 mx-auto max-w-sm"
          style={{ backgroundColor: 'rgba(153,27,27,0.05)', border: '1px solid rgba(153,27,27,0.1)' }}>
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#991B1B' }} />
          <p className="text-[11px]" style={{ color: '#991B1B' }}>
            <span style={{ fontWeight: 500 }}>47 people</span> ordered in the last 24 hours
          </p>
        </div>

        {/* Logo — hero mark */}
        <div className="text-center mb-8">
          <img
            data-testid="landing-hero-logo"
            src="/lunora_logo.png"
            alt="Lunora's"
            className="w-40 sm:w-48 md:w-56 h-auto mx-auto"
            style={{ display: 'block' }}
          />
          <div
            className="w-16 h-[2px] mx-auto mt-1"
            style={{ background: 'linear-gradient(90deg, #E5B2B9 0%, #D4AF37 100%)' }}
          />
        </div>

        {/* Main Headline */}
        <div className="max-w-lg mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl leading-tight mb-4"
            style={{ 
              fontFamily: 'Playfair Display, serif', 
              color: '#0F0F0F',
              letterSpacing: '-0.02em'
            }}
          >
            Custom-Tailored Fashion That Actually Fits
          </h2>
          
          <p 
            className="text-base leading-relaxed mb-3"
            style={{ color: '#595959' }}
          >
            Upload one photo. Get AI-measured. Receive a garment made for your body by India's finest tailors.
          </p>

          {/* Star Rating + Reviews */}
          <div className="flex items-center justify-center gap-1.5 mb-8">
            {[1,2,3,4,5].map(i => (
              <Star key={i} className="w-4 h-4" strokeWidth={1.5} style={{ color: '#D4AF37', fill: '#D4AF37' }} />
            ))}
            <span className="text-[12px] ml-1" style={{ color: '#0F0F0F', fontWeight: 500 }}>4.9</span>
            <span className="text-[12px]" style={{ color: '#595959' }}>(2,847 reviews)</span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col gap-3 max-w-sm mx-auto">
          <button
            data-testid="start-ai-sizing-btn"
            onClick={() => navigate('/gender-selection')}
            className="w-full py-4 px-8 text-white text-sm tracking-[0.08em] uppercase transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden"
            style={{ 
              background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)',
              fontWeight: 600
            }}
          >
            <Scan className="w-5 h-5" strokeWidth={1.5} />
            Get My Perfect Fit — Free
          </button>
          <p className="text-[10px] text-center" style={{ color: '#595959' }}>
            No payment needed. AI sizing takes 30 seconds.
          </p>
          
          <button
            data-testid="explore-designs-btn"
            onClick={() => navigate('/gender-selection')}
            className="w-full py-4 px-8 text-sm tracking-[0.08em] uppercase transition-all duration-300 flex items-center justify-center gap-3"
            style={{ 
              backgroundColor: '#0F0F0F',
              color: '#FFFFFF',
              fontWeight: 500
            }}
          >
            <Palette className="w-5 h-5" strokeWidth={1.5} />
            Browse 69+ Designs
          </button>
        </div>

        {/* Tailor Partner Login Link */}
        <div className="text-center mt-5">
          <button
            data-testid="tailor-partner-login-link"
            onClick={() => navigate('/tailor-login')}
            className="inline-flex items-center gap-2 text-sm transition-colors"
            style={{ color: '#595959' }}
            onMouseOver={(e) => e.currentTarget.style.color = '#D4AF37'}
            onMouseOut={(e) => e.currentTarget.style.color = '#595959'}
          >
            <Scissors className="w-4 h-4" strokeWidth={1.5} />
            <span style={{ letterSpacing: '0.02em' }}>Tailor Partner Login</span>
            <ChevronRight className="w-3 h-3" strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Personalized Recommendations */}
      <RecentlyViewedSection />
      <ForYouSection isLoggedIn={!!user} />

      {/* Customer Reviews Carousel */}
      <div className="py-8 px-6" style={{ backgroundColor: '#FFFFFF' }}>
        <p className="text-[10px] tracking-[0.2em] uppercase text-center mb-5" style={{ color: '#595959' }}>What Customers Say</p>
        <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 max-w-lg mx-auto">
          {[
            { name: 'Priya S.', city: 'Jaipur', text: 'The AI sizing was shockingly accurate. My blouse fit perfectly on the first try!', stars: 5 },
            { name: 'Rahul M.', city: 'Delhi', text: 'Ordered a sherwani for my wedding. Better quality than stores charging 3x more.', stars: 5 },
            { name: 'Anita P.', city: 'Mumbai', text: 'Free alterations gave me confidence to try. Now I\'m a repeat customer!', stars: 5 },
          ].map((r, i) => (
            <div key={i} className="flex-shrink-0 w-[260px] bg-[#FAF9F6] border p-4" style={{ borderColor: '#E5E5E5' }}>
              <div className="flex items-center gap-0.5 mb-2">
                {Array.from({length: r.stars}).map((_, j) => (
                  <Star key={j} className="w-3 h-3" strokeWidth={1.5} style={{ color: '#D4AF37', fill: '#D4AF37' }} />
                ))}
              </div>
              <p className="text-[12px] leading-relaxed mb-2" style={{ color: '#0F0F0F' }}>"{r.text}"</p>
              <p className="text-[10px]" style={{ color: '#595959' }}>{r.name}, {r.city}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Social Proof Section */}
      <div className="py-12 px-6" style={{ backgroundColor: '#FFFFFF' }}>
        <p 
          className="text-xs tracking-[0.2em] uppercase text-center mb-8"
          style={{ color: '#595959' }}
        >
          Trusted by Thousands
        </p>
        <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
          {socialProof.map((item, index) => (
            <div key={index} className="text-center">
              <item.icon 
                className="w-6 h-6 mx-auto mb-2" 
                style={{ color: '#D4AF37' }} 
                strokeWidth={1.5} 
              />
              <p 
                className="text-2xl font-medium"
                style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}
              >
                {item.value}
              </p>
              <p className="text-xs mt-1" style={{ color: '#595959' }}>{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Trust Badges */}
      <div className="py-12 px-6">
        <p 
          className="text-xs tracking-[0.2em] uppercase text-center mb-8"
          style={{ color: '#595959' }}
        >
          Why Choose Lunora's
        </p>
        <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
          {trustBadges.map((badge, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 p-4 bg-white border border-[#E5E5E5]"
            >
              <div 
                className="w-10 h-10 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)' }}
              >
                <badge.icon className="w-5 h-5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: '#0F0F0F' }}>{badge.label}</p>
                <p className="text-xs" style={{ color: '#595959' }}>{badge.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 px-6 border-t" style={{ borderColor: '#E5E5E5', backgroundColor: '#FFFFFF' }}>
        <div className="max-w-lg mx-auto">
          {/* Row 1: Company Links */}
          <div className="flex items-center justify-center gap-3 mb-3 flex-wrap">
            {[
              { label: 'About', path: '/info/about' },
              { label: 'FAQ', path: '/info/faq' },
              { label: 'Help', path: '/info/help' },
              { label: 'Contact', path: '/info/contact' },
            ].map((link, i) => (
              <React.Fragment key={link.path}>
                {i > 0 && <span className="text-xs" style={{ color: '#BFBFBF' }}>|</span>}
                <button onClick={() => navigate(link.path)}
                  className="text-xs tracking-[0.05em]" style={{ color: '#595959' }}>{link.label}</button>
              </React.Fragment>
            ))}
          </div>
          {/* Row 2: Legal + Tailor */}
          <div className="flex items-center justify-center gap-3 mb-3 flex-wrap">
            {[
              { label: 'Terms', path: '/info/terms' },
              { label: 'Privacy', path: '/info/privacy' },
              { label: 'Tailor Login', path: '/tailor-login' },
            ].map((link, i) => (
              <React.Fragment key={link.path}>
                {i > 0 && <span className="text-xs" style={{ color: '#BFBFBF' }}>|</span>}
                <button onClick={() => navigate(link.path)}
                  className="text-xs tracking-[0.05em]" style={{ color: '#595959' }}>{link.label}</button>
              </React.Fragment>
            ))}
          </div>
          <p className="text-center text-[10px] tracking-[0.1em]" style={{ color: '#BFBFBF' }}>
            LUNORA'S &mdash; PREMIUM CUSTOM FASHION
          </p>
        </div>
      </div>

      {/* Bottom CTA */}
      <div 
        className="sticky bottom-0 py-3 px-6 border-t"
        style={{ 
          backgroundColor: 'rgba(255,255,255,0.95)', 
          backdropFilter: 'blur(12px)',
          borderColor: '#E5E5E5'
        }}
      >
        <button
          data-testid="bottom-cta-btn"
          onClick={() => navigate('/gender-selection')}
          className="w-full py-4 text-white text-sm tracking-[0.08em] uppercase"
          style={{ 
            background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)',
            fontWeight: 600
          }}
        >
          Start Free AI Sizing
        </button>
        <p className="text-[9px] text-center mt-1.5" style={{ color: '#BFBFBF' }}>
          Free alterations {'\u2022'} 7-day money back {'\u2022'} No commitment
        </p>
      </div>

      {/* CSS Animation for menu */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
